/**
 * CreateToolForm
 *
 * A form component for creating custom integrations
 *
 * Primary responsibilities:
 * - Provide AI-powered integration generation
 * - Allow manual integration creation with OpenAPI schema
 * - Validate integration schemas
 * - Handle creation and success feedback
 */

import { FC, useContext, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { ChatbotUIContext } from '@/context/context'
import { TOOL_DESCRIPTION_MAX, TOOL_NAME_MAX } from '@/db/limits'
import { validateOpenAPI } from '@/lib/openapi-conversion'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { runGeneratePluginRequest } from '@/lib/services/run-generate-plugin-client'
import { Tables } from '@/supabase/types'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TextareaAutosize } from '@/components/ui/textarea-autosize'
import { AIGenerationInput } from '@/components/ui/ai-generation-input'
import { SharingField } from '@/components/sidebar/items/all/sharing-field'
import { IconSparkle2 } from '@tabler/icons-react'
import { Loader2 } from 'lucide-react'
import {
  AuthMetadataReview,
  CredentialFieldsEditor,
  HeaderRowsEditor,
  OpenApiSchemaEditor,
  createCredentialFieldsFromGeneration,
  createCredentialFieldsFromSchema,
  getEditableHeaderRows,
  getIntegrationAuthMetadataFromGeneration,
  getIntegrationAuthMetadataFromSchema,
  getMissingRequiredCredentialFields,
  headersToRows,
  normalizeHeaderRecord,
  parseStoredHeaderRecord,
  resolveHeadersForSave,
  resolveSchemaForSave,
  type IntegrationAuthMetadata,
  type HeaderRow,
  type IntegrationCredentialField,
  type IntegrationGenerationResponse
} from '@/components/sidebar/items/tools/custom-integration-form-fields'

interface CreateToolFormProps {
  existingTool?: Tables<'tools'>
  onSuccess?: () => void
  onCancel?: () => void
}

type VerificationState =
  | { message: string; status: 'failed' | 'verified' }
  | { message: ''; status: 'idle' }

function stringifyStoredSchema(schema: unknown): string {
  if (typeof schema === 'string') {
    try {
      return JSON.stringify(JSON.parse(schema), null, 2)
    } catch {
      return schema
    }
  }

  return JSON.stringify(schema || {}, null, 2)
}

function parseSchema(schema: string): unknown {
  try {
    return JSON.parse(schema)
  } catch {
    throw new Error('Schema must be valid JSON')
  }
}

function getSchemaServerUrl(schema: unknown): string {
  const parsedSchema = typeof schema === 'string' ? parseSchema(schema) : schema

  if (!isRecord(parsedSchema) || !Array.isArray(parsedSchema.servers)) {
    return ''
  }

  const server = parsedSchema.servers.find(isRecord)

  return typeof server?.url === 'string' ? server.url : ''
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getSavedTool(value: unknown): Tables<'tools'> {
  if (!isRecord(value) || !isRecord(value.tool) || typeof value.tool.id !== 'string') {
    throw new Error('Integration was saved but the response was invalid')
  }

  return value.tool as Tables<'tools'>
}

export const CreateToolForm: FC<CreateToolFormProps> = ({ existingTool, onSuccess, onCancel }) => {
  const { t } = useTranslation()
  const { selectedWorkspace, profile, setTools } = useContext(ChatbotUIContext)
  const isEditing = Boolean(existingTool)

  // Create integration state
  const [toolName, setToolName] = useState(existingTool?.name || '')
  const [toolDescription, setToolDescription] = useState(existingTool?.description || '')
  const [toolUrl, setToolUrl] = useState(existingTool?.url || '')
  const [sharing, setSharing] = useState(existingTool?.sharing || 'workspace')
  const [credentialFields, setCredentialFields] = useState<IntegrationCredentialField[]>([])
  const [headerRows, setHeaderRows] = useState<HeaderRow[]>(() =>
    headersToRows(parseStoredHeaderRecord(existingTool?.custom_headers))
  )
  const [schema, setSchema] = useState(() =>
    existingTool ? stringifyStoredSchema(existingTool.schema) : ''
  )
  const [schemaError, setSchemaError] = useState('')
  const [authMetadata, setAuthMetadata] = useState<IntegrationAuthMetadata | null>(() =>
    existingTool
      ? getIntegrationAuthMetadataFromSchema(stringifyStoredSchema(existingTool.schema))
      : null
  )
  const [isGenerating, setIsGenerating] = useState(false)
  const [showAIInput, setShowAIInput] = useState(!existingTool)
  const [userPrompt, setUserPrompt] = useState('')
  const [hasGenerated, setHasGenerated] = useState(Boolean(existingTool))
  const [isCreating, setIsCreating] = useState(false)
  const [isFixingSchema, setIsFixingSchema] = useState(false)
  const [isVerifyingConnection, setIsVerifyingConnection] = useState(false)
  const [verification, setVerification] = useState<VerificationState>({
    message: '',
    status: 'idle'
  })

  useEffect(() => {
    if (!existingTool) {
      setToolName('')
      setToolDescription('')
      setToolUrl('')
      setSharing('workspace')
      setCredentialFields([])
      setHeaderRows([])
      setSchema('')
      setSchemaError('')
      setAuthMetadata(null)
      setShowAIInput(true)
      setUserPrompt('')
      setHasGenerated(false)
      setVerification({ message: '', status: 'idle' })
      return
    }

    const nextSchema = stringifyStoredSchema(existingTool.schema)

    setToolName(existingTool.name || '')
    setToolDescription(existingTool.description || '')
    setToolUrl(existingTool.url || '')
    setSharing(existingTool.sharing || 'workspace')
    const existingHeaders = parseStoredHeaderRecord(existingTool.custom_headers)
    const existingFields = createCredentialFieldsFromSchema(nextSchema, existingHeaders)

    setCredentialFields(existingFields)
    setHeaderRows(getEditableHeaderRows(existingHeaders, existingFields))
    setSchema(nextSchema)
    setSchemaError('')
    setAuthMetadata(getIntegrationAuthMetadataFromSchema(nextSchema))
    setShowAIInput(false)
    setUserPrompt('')
    setHasGenerated(true)
    setVerification({ message: '', status: 'idle' })
  }, [existingTool])

  const markDraftChanged = () => {
    setVerification({ message: '', status: 'idle' })
  }

  const resetForm = () => {
    setToolName('')
    setToolDescription('')
    setToolUrl('')
    setSharing('workspace')
    setCredentialFields([])
    setHeaderRows([])
    setSchema('')
    setSchemaError('')
    setAuthMetadata(null)
    setIsGenerating(false)
    setShowAIInput(true)
    setUserPrompt('')
    setHasGenerated(false)
    setVerification({ message: '', status: 'idle' })
  }

  const handleIntegrationGeneration = async (description: string) => {
    try {
      setIsGenerating(true)
      setUserPrompt(description)
      markDraftChanged()

      const data = (await runGeneratePluginRequest(
        authenticatedFetch,
        description
      )) as IntegrationGenerationResponse
      const headers = normalizeHeaderRecord(data.custom_headers)

      setToolName(data.name)
      setToolDescription(data.description)
      setCredentialFields(createCredentialFieldsFromGeneration(data))
      setHeaderRows(headersToRows(headers))
      setSchema(JSON.stringify(data.schema, null, 2))
      setAuthMetadata(getIntegrationAuthMetadataFromGeneration(data))

      try {
        await validateOpenAPI(data.schema)
        setSchemaError('')
      } catch (error) {
        setSchemaError(error instanceof Error ? error.message : 'Invalid schema')
      }

      setHasGenerated(true)
      setShowAIInput(false)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('Failed to generate integration')
      )
    } finally {
      setIsGenerating(false)
    }
  }

  const handleEditPrompt = () => {
    setShowAIInput(true)
  }

  const handleManualCreate = () => {
    markDraftChanged()
    setAuthMetadata(null)
    setShowAIInput(false)
    setHasGenerated(true)
  }

  const handleFixSchema = async () => {
    if (!schema || !schemaError) return

    try {
      setIsFixingSchema(true)

      const response = await authenticatedFetch('/api/chat/marshmallow/fix-schema', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentSchema: (() => {
            try {
              return JSON.parse(schema)
            } catch {
              return { error: 'Invalid JSON' }
            }
          })(),
          validationError: schemaError,
          toolName,
          toolDescription,
          customHeaders: resolveHeadersForSave(headerRows, credentialFields)
        })
      })

      if (!response.ok) throw new Error('Failed to fix schema')

      const data = await response.json()

      if (data.success && data.fixedSchema) {
        const fixedSchema = JSON.stringify(data.fixedSchema, null, 2)
        setSchema(fixedSchema)
        setSchemaError('')
        setAuthMetadata(getIntegrationAuthMetadataFromSchema(fixedSchema))
        markDraftChanged()
        toast.success(t('Schema fixed successfully'))

        // Show changes applied if any
        if (data.changesApplied && data.changesApplied.length > 0) {
          toast.info(`Changes applied: ${data.changesApplied.join(', ')}`)
        }
      } else {
        throw new Error(data.error || 'Schema fix failed')
      }
    } catch (error) {
      console.error('Error fixing schema:', error)
      toast.error(t('Failed to fix schema'))
    } finally {
      setIsFixingSchema(false)
    }
  }

  const handleCreateTool = async () => {
    if (!profile) {
      console.error('Missing profile or workspace:', { profile, selectedWorkspace })
      return
    }

    if (!profile.user_id) {
      console.error('Profile user_id is missing:', profile)
      toast.error(t('User profile not loaded. Please refresh the page.'))
      return
    }

    try {
      setIsCreating(true)
      const missingCredentials = getMissingRequiredCredentialFields(credentialFields)

      if (missingCredentials.length > 0) {
        throw new Error(
          `Missing required credentials: ${missingCredentials
            .map((field) => field.label)
            .join(', ')}`
        )
      }

      // Ensure user_id is valid UUID
      if (!profile.user_id || profile.user_id.trim() === '') {
        throw new Error('Invalid user ID: user profile not properly loaded')
      }

      if (!existingTool && !selectedWorkspace) {
        throw new Error('Workspace is required to create an integration')
      }

      const parsedSchema = parseSchema(schema)
      const schemaForSave = resolveSchemaForSave(parsedSchema, credentialFields)
      const response = await authenticatedFetch('/api/chat/marshmallow/generated-integration', {
        method: existingTool ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          authOptions: authMetadata?.authOptions,
          authStrategy: authMetadata?.strategy,
          customHeaders: resolveHeadersForSave(headerRows, credentialFields),
          description: toolDescription,
          draft: false,
          environmentFields: credentialFields.map((field) => ({
            description: field.description,
            hasSavedValue: field.hasSavedValue,
            headerName: field.headerName,
            key: field.key,
            label: field.label,
            placeholder: field.placeholder,
            required: field.required,
            secret: field.secret,
            value: field.value,
            valueTemplate: field.valueTemplate,
            vaultSecretId: field.vaultSecretId
          })),
          name: toolName,
          schema: schemaForSave,
          sharing,
          toolId: existingTool?.id,
          url: getSchemaServerUrl(schemaForSave) || toolUrl,
          userInput: userPrompt,
          workspaceId: selectedWorkspace?.id
        })
      })
      const data: unknown = await response.json()

      if (!response.ok) {
        const message =
          isRecord(data) && typeof data.error === 'string'
            ? data.error
            : 'Failed to save integration'

        throw new Error(message)
      }

      const savedTool = getSavedTool(data)

      setTools((currentTools) =>
        currentTools.some((tool) => tool.id === savedTool.id)
          ? currentTools.map((tool) => (tool.id === savedTool.id ? savedTool : tool))
          : [savedTool, ...currentTools]
      )

      toast.success(
        isEditing ? t('Integration updated successfully') : t('Integration created successfully')
      )
      resetForm()
      onSuccess?.()
    } catch (error) {
      console.error('Error creating integration:', error)
      toast.error(error instanceof Error ? error.message : t('Failed to create integration'))
    } finally {
      setIsCreating(false)
    }
  }

  const handleVerifyConnection = async () => {
    try {
      setIsVerifyingConnection(true)
      const missingCredentials = getMissingRequiredCredentialFields(credentialFields)

      if (missingCredentials.length > 0) {
        throw new Error(
          `Missing required credentials: ${missingCredentials
            .map((field) => field.label)
            .join(', ')}`
        )
      }

      const schemaForVerify = resolveSchemaForSave(parseSchema(schema), credentialFields)
      const response = await authenticatedFetch('/api/chat/marshmallow/verify-plugin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customHeaders: resolveHeadersForSave(headerRows, credentialFields),
          saveVerification: Boolean(existingTool?.id),
          schema: schemaForVerify,
          toolId: existingTool?.id
        })
      })
      const data: unknown = await response.json()
      const responseMessage =
        isRecord(data) && typeof data.message === 'string'
          ? data.message
          : t('Connection verified')
      const responseError =
        isRecord(data) && typeof data.error === 'string'
          ? data.error
          : t('Connection check failed')
      const success = isRecord(data) && data.success === true

      if (!response.ok || !success) {
        setVerification({
          message: responseError,
          status: 'failed'
        })
        throw new Error(responseError)
      }

      setVerification({ message: responseMessage, status: 'verified' })
      toast.success(responseMessage)
    } catch (error) {
      setVerification({
        message: error instanceof Error ? error.message : t('Connection check failed'),
        status: 'failed'
      })
      toast.error(error instanceof Error ? error.message : t('Connection check failed'))
    } finally {
      setIsVerifyingConnection(false)
    }
  }

  return (
    <motion.div
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="flex min-h-0 flex-1 flex-col space-y-4"
    >
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-2">
        {showAIInput && !hasGenerated && (
          <AIGenerationInput
            onGenerate={handleIntegrationGeneration}
            isGenerating={isGenerating}
            placeholder={t('Describe the custom integration you want to create...')}
            onSkip={handleManualCreate}
          />
        )}

        {showAIInput && hasGenerated && (
          <div className="space-y-2">
            <Label>{t('Your prompt')}</Label>
            <TextareaAutosize
              className="border-input bg-background w-full rounded-md border px-3 py-2"
              value={userPrompt}
              onValueChange={(value) => {
                setUserPrompt(value)
                markDraftChanged()
              }}
              minRows={2}
              maxRows={4}
            />
            <div className="flex gap-2">
              <Button onClick={handleEditPrompt} variant="outline" className="flex-1">
                {t('Edit')}
              </Button>
              <Button
                onClick={() => handleIntegrationGeneration(userPrompt)}
                disabled={isGenerating || !userPrompt.trim()}
                className="flex-1 gap-2"
              >
                {isGenerating ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <IconSparkle2 className="size-3.5" />
                )}
                <span>{isGenerating ? t('Regenerating...') : t('Regenerate')}</span>
              </Button>
            </div>
          </div>
        )}

        {(!showAIInput || hasGenerated) && (
          <>
            <div className="text-muted-foreground space-y-1.5 text-sm">
              <div>
                {isEditing
                  ? t('Edit this custom integration.')
                  : t('Create a custom integration.')}
              </div>
              <div>
                {t('Your API')} <span className="font-bold">*{t('must')}*</span>{' '}
                {t('be compatible with the OpenAI SDK.')}
              </div>
            </div>

            <div className="space-y-1">
              <Label>{t('Name')}</Label>
              <Input
                placeholder={t('Integration name...')}
                value={toolName}
                onChange={(e) => {
                  setToolName(e.target.value)
                  markDraftChanged()
                }}
                maxLength={TOOL_NAME_MAX}
              />
            </div>

            <div className="space-y-1">
              <Label>{t('Description')}</Label>
              <Input
                placeholder={t('Integration description...')}
                value={toolDescription}
                onChange={(e) => {
                  setToolDescription(e.target.value)
                  markDraftChanged()
                }}
                maxLength={TOOL_DESCRIPTION_MAX}
              />
            </div>

            <SharingField
              value={sharing}
              onChange={(value) => {
                setSharing(value)
                markDraftChanged()
              }}
            />

            <AuthMetadataReview metadata={authMetadata} />

            <CredentialFieldsEditor
              fields={credentialFields}
              onChange={(fields) => {
                setCredentialFields(fields)
                markDraftChanged()
              }}
            />

            <HeaderRowsEditor
              rows={headerRows}
              onChange={(rows) => {
                setHeaderRows(rows)
                markDraftChanged()
              }}
            />

            <OpenApiSchemaEditor
              schema={schema}
              schemaError={schemaError}
              onSchemaChange={(value) => {
                setSchema(value)
                setAuthMetadata(getIntegrationAuthMetadataFromSchema(value))
                markDraftChanged()
              }}
              onSchemaError={setSchemaError}
              validateSchema={validateOpenAPI}
            />

            <div className="space-y-3 rounded-md border p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">{t('Review and test')}</div>
                  <div className="text-muted-foreground text-xs">
                    {t('Review the generated fields. Create saves the integration.')}
                  </div>
                </div>
              </div>

              <div className="grid gap-2 text-xs sm:grid-cols-2">
                <div className="rounded-md border px-2 py-1.5">
                  <div className="font-medium">{t('Review')}</div>
                  <div className="text-muted-foreground">
                    {toolName && schema && !schemaError ? t('Ready') : t('Needs attention')}
                  </div>
                </div>
                <div className="rounded-md border px-2 py-1.5">
                  <div className="font-medium">{t('Test')}</div>
                  <div
                    className={
                      verification.status === 'failed'
                        ? 'text-red-500'
                        : verification.status === 'verified'
                          ? 'text-emerald-600'
                          : 'text-muted-foreground'
                    }
                  >
                    {verification.status === 'verified'
                      ? t('Verified')
                      : verification.status === 'failed'
                        ? t('Failed')
                        : t('Not tested')}
                  </div>
                </div>
              </div>

              {verification.message && (
                <div
                  className={
                    verification.status === 'failed'
                      ? 'text-xs text-red-500'
                      : 'text-xs text-emerald-600'
                  }
                >
                  {verification.message}
                </div>
              )}

              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleVerifyConnection}
                  disabled={isVerifyingConnection || !schema || !!schemaError}
                >
                  {isVerifyingConnection ? t('Checking...') : t('Verify connection')}
                </Button>
              </div>
            </div>

            <div>
              {schemaError && (
                <div className="flex items-center gap-2 text-xs text-red-500">
                  <span className="flex-1">{schemaError}</span>
                  <button
                    onClick={handleFixSchema}
                    disabled={isFixingSchema}
                    className="flex items-center gap-1 rounded bg-red-50 px-2 py-1 text-xs text-red-600 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                    title={t('Fix schema with AI')}
                  >
                    {isFixingSchema ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : (
                      <IconSparkle2 className="size-3" />
                    )}
                    <span>{isFixingSchema ? t('Fixing...') : t('Fix')}</span>
                  </button>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={onCancel} className="flex-1">
                {t('Cancel')}
              </Button>
              <Button
                onClick={handleCreateTool}
                disabled={isCreating || !toolName || !schema || !!schemaError}
                className="flex-1"
              >
                {isCreating
                  ? isEditing
                    ? t('Saving...')
                    : t('Creating...')
                  : isEditing
                    ? t('Save changes')
                    : t('Create integration')}
              </Button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  )
}
